import { useEffect, useState } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { CookieService } from '../services/cookieService';

interface ThemeConfig {
  colorScheme: string;
  fontFamily: string;
  borderRadius: string;
  fontSize: string;
}

const defaultThemeConfig: ThemeConfig = {
  colorScheme: 'green',
  fontFamily: 'Inter, sans-serif',
  borderRadius: 'sm',
  fontSize: 'md',
};

export const useTheme = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(defaultThemeConfig);

  // Load theme config from localStorage on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('compassai-theme-config');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setThemeConfig({ ...defaultThemeConfig, ...config });
        applyThemeToDOM({ ...defaultThemeConfig, ...config });
      } catch (error) {
        console.error('Failed to parse theme config:', error);
      }
    }
  }, []);

  const applyThemeToDOM = (config: ThemeConfig) => {
    const root = document.documentElement;
    
    // Apply font family
    root.style.setProperty('--chakra-fonts-body', config.fontFamily);
    root.style.setProperty('--chakra-fonts-heading', config.fontFamily);

    // Apply border radius
    const radiusMap: Record<string, string> = {
      'none': '0',
      'xs': '0.125rem',
      'sm': '0.25rem', 
      'md': '0.375rem',
      'lg': '0.5rem',
      'xl': '0.75rem',
      '2xl': '1rem',
    };
    const radiusValue = radiusMap[config.borderRadius] || '0.25rem';
    root.style.setProperty('--chakra-radii-base', radiusValue);
    root.style.setProperty('--chakra-radii-md', radiusValue);
    root.style.setProperty('--chakra-radii-lg', radiusValue);
    root.style.setProperty('--chakra-radii-xl', radiusValue);

    // Apply font size
    const fontSizeMap: Record<string, string> = {
      'xs': '0.75rem',
      'sm': '0.875rem',
      'md': '1rem',
      'lg': '1.125rem',
      'xl': '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
    };
    const baseFontSize = fontSizeMap[config.fontSize] || '1rem';
    root.style.setProperty('--chakra-fontSizes-md', baseFontSize);
    
    // Scale other font sizes proportionally
    const baseSize = parseFloat(baseFontSize);
    root.style.setProperty('--chakra-fontSizes-xs', `${baseSize * 0.75}rem`);
    root.style.setProperty('--chakra-fontSizes-sm', `${baseSize * 0.875}rem`);
    root.style.setProperty('--chakra-fontSizes-lg', `${baseSize * 1.125}rem`);
    root.style.setProperty('--chakra-fontSizes-xl', `${baseSize * 1.25}rem`);
    root.style.setProperty('--chakra-fontSizes-2xl', `${baseSize * 1.5}rem`);

    // Apply color scheme - map to chakra color palettes
    const colorSchemeMap: Record<string, any> = {
      'gray': {
        50: '#f7fafc', 100: '#edf2f7', 200: '#e2e8f0', 300: '#cbd5e0', 400: '#a0aec0',
        500: '#718096', 600: '#4a5568', 700: '#2d3748', 800: '#1a202c', 900: '#171923'
      },
      'red': {
        50: '#fff5f5', 100: '#fed7d7', 200: '#feb2b2', 300: '#fc8181', 400: '#f56565',
        500: '#e53e3e', 600: '#c53030', 700: '#9b2c2c', 800: '#822727', 900: '#63171b'
      },
      'orange': {
        50: '#fffaf0', 100: '#feebc8', 200: '#fbd38d', 300: '#f6ad55', 400: '#ed8936',
        500: '#dd6b20', 600: '#c05621', 700: '#9c4221', 800: '#7b341e', 900: '#652b19'
      },
      'yellow': {
        50: '#fffff0', 100: '#fefcbf', 200: '#faf089', 300: '#f6e05e', 400: '#ecc94b',
        500: '#d69e2e', 600: '#b7791f', 700: '#975a16', 800: '#744210', 900: '#5f370e'
      },
      'green': {
        50: '#f0fff4', 100: '#c6f6d5', 200: '#9ae6b4', 300: '#68d391', 400: '#48bb78',
        500: '#38a169', 600: '#2f855a', 700: '#276749', 800: '#22543d', 900: '#1c4532'
      },
      'teal': {
        50: '#e6fffa', 100: '#b2f5ea', 200: '#81e6d9', 300: '#4fd1c7', 400: '#38b2ac',
        500: '#319795', 600: '#2c7a7b', 700: '#285e61', 800: '#234e52', 900: '#1d4044'
      },
      'blue': {
        50: '#ebf8ff', 100: '#bee3f8', 200: '#90cdf4', 300: '#63b3ed', 400: '#4299e1',
        500: '#3182ce', 600: '#2b77cb', 700: '#2c5282', 800: '#2a4365', 900: '#1a365d'
      },
      'cyan': {
        50: '#edfdfd', 100: '#c4f1f9', 200: '#9decf9', 300: '#76e4f7', 400: '#0bc5ea',
        500: '#00b5d8', 600: '#00a3c4', 700: '#0987a0', 800: '#086f83', 900: '#065666'
      },
      'purple': {
        50: '#faf5ff', 100: '#e9d8fd', 200: '#d6bcfa', 300: '#b794f6', 400: '#9f7aea',
        500: '#805ad5', 600: '#6b46c1', 700: '#553c9a', 800: '#44337a', 900: '#322659'
      },
      'pink': {
        50: '#fff5f7', 100: '#fed7e2', 200: '#fbb6ce', 300: '#f687b3', 400: '#ed64a6',
        500: '#d53f8c', 600: '#b83280', 700: '#97266d', 800: '#702459', 900: '#521b41'
      }
    };

    const colors = colorSchemeMap[config.colorScheme];
    if (colors) {
      Object.entries(colors).forEach(([shade, color]) => {
        root.style.setProperty(`--chakra-colors-primary-${shade}`, color as string);
      });
    }
  };

  const updateThemeConfig = (newConfig: Partial<ThemeConfig>) => {
    const updatedConfig = { ...themeConfig, ...newConfig };
    setThemeConfig(updatedConfig);
    applyThemeToDOM(updatedConfig);
    const configString = JSON.stringify(updatedConfig);
    localStorage.setItem('compassai-theme-config', configString);
    
    // Also update cookie if user has remember me enabled
    if (CookieService.getCookie('rememberMe') === 'true') {
      CookieService.setCookie('themeConfig', configString, 30);
    }
  };

  const resetTheme = () => {
    setThemeConfig(defaultThemeConfig);
    applyThemeToDOM(defaultThemeConfig);
    localStorage.removeItem('compassai-theme-config');
    
    // Also remove from cookies if user has remember me enabled
    if (CookieService.getCookie('rememberMe') === 'true') {
      CookieService.deleteCookie('themeConfig');
    }
  };

  const toggleColorModeWithCookie = () => {
    toggleColorMode();
    
    // Update cookie if user has remember me enabled
    if (CookieService.getCookie('rememberMe') === 'true') {
      // Since toggleColorMode is asynchronous, we need to determine the new mode
      const newMode = colorMode === 'light' ? 'dark' : 'light';
      CookieService.setCookie('colorMode', newMode, 30);
    }
  };

  return {
    themeConfig,
    updateThemeConfig,
    resetTheme,
    colorMode,
    toggleColorMode: toggleColorModeWithCookie,
  };
};